import { Box } from '@withjoy/joykit';
import React, { useEffect } from 'react';
import { ProductDetails } from '../../components/ProductDetails';
import { useCatalogProductDetails } from './CatalogProductDetails.controller';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { StaticRouterProps } from '@shared/core';
import { useTranslation } from '@shared/core';
import { Helmet } from 'react-helmet-async';
import {
  useCatalogProductDetailsHealth,
  wrapWithCatalogProductDetailsHealthProvider,
} from '@apps/registry/common/components/Catalog/routes/CatalogProductDetails/CatalogProductDetails.health';
import { Seo } from '@shared/components/Seo';
import { getMetaDescription } from '../../utils/metaDescription';
import PageNotFound from '@shared/components/PageNotFound/PageNotFound';
import { useRegistryShopping } from '@apps/registry/admin/RegistryShopping.provider';

export const CatalogProductDetails = wrapWithCatalogProductDetailsHealthProvider((props: { catalogItemId: string; staticContext?: StaticRouterProps['context'] }) => {
  const { addCatalogItemToRegistry, addCatalogItemLoading, hasError, error, product, productLoading, getCatalogProductDetails, isProductInRegistry } =
    useCatalogProductDetails(props);

  const isMobileOrTablet = useIsMobileOrTablet();

  const { t } = useTranslation('curationsRegistry');
  const { isEnabled: isShoppingEnabled } = useRegistryShopping();

  const curationsTranslations = t('curations');
  const defaultDepartmentTitle = curationsTranslations.defaultDepartmentTitle();

  const { onLoadComplete, onLoadFailure } = useCatalogProductDetailsHealth();
  useEffect(() => {
    if (error) {
      onLoadFailure('GetCatalogProductDetailsQuery failed', error);
    } else if (product) {
      onLoadComplete();
    }
  }, [error, onLoadFailure, product, onLoadComplete]);

  const handleHover = (pid?: string, isHoverExit: boolean = false) => {
    const catalogItemId = isHoverExit ? props.catalogItemId : pid;
    if (catalogItemId) {
      getCatalogProductDetails({
        variables: {
          catalogItemId: catalogItemId!,
          withPdpSections: true,
          withProductRating: true,
          withInventory: isShoppingEnabled,
        },
      });
    }
  };

  const metaDescription = getMetaDescription(
    product?.shortDescription?.trim() ||
      product?.longDescription?.trim() ||
      product?.pdpSections?.find(section => section.sectionType === 'details')?.sectionTree?.elements[2].text,
    product?.title,
    product?.brand?.name,
  );

  if (!product && !productLoading) {
    if (props.staticContext) {
      props.staticContext.statusCode = 404;
    }
    return <PageNotFound />;
  }

  return (
    <Box
      paddingX={['24px', null, null, null, null, '88px']}
      paddingBottom={`${180 / 16}rem`}
      paddingTop={isMobileOrTablet ? 8 : 0}
      margin="auto"
      marginTop={[0, null, 5]}
      maxWidth={'1568px'}
    >
      {product && (
        <>
          <Seo
            title={`${product.brand?.name} ${product.title} | Joy`}
            description={metaDescription}
          />
          <Helmet>
            {product.title && (
              <meta
                property="og:title"
                content={product.title}
              />
            )}
            {!!product.photos[0]?.url && (
              <meta
                property="og:image"
                content={product.photos[0].url}
              />
            )}
          </Helmet>
        </>
      )}
      <ProductDetails
        key={product?.id}
        showSkeleton={!product || !!hasError}
        product={product}
        addCatalogItem={addCatalogItemToRegistry}
        addCatalogItemLoading={addCatalogItemLoading}
        defaultDepartmentTitle={defaultDepartmentTitle}
        isDialog={false}
        showFooter={true}
        handleHover={handleHover}
        isItemAddedToRegistry={isProductInRegistry(props.catalogItemId)}
      />
    </Box>
  );
});

CatalogProductDetails.displayName = 'CatalogProductDetails';
